import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FwButtonComponent,
  FwLayoutPanelComponent,
  FwPopoverComponent,
  FwTypeaheadComponent,
  FwWrappedInputComponent,
} from '@flywheel-io/vision';
import { isEqual } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';

import { MaterialModule } from 'app/src/material.module';

import { CoreApiService } from '../../core-api.service';
import { SearchReturnType } from '../../shared/models/search/search-query.model';

export interface TagFilter {
  contains: string[];
  excludes: string[];
}

@Component({
  host: { 'test-id': 'tag-select-menu' },
  selector: 'tag-select-menu',
  templateUrl: './tag-select-menu.component.html',
  styleUrls: ['./tag-select-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    FwLayoutPanelComponent,
    FwButtonComponent,
    FwWrappedInputComponent,
    FwTypeaheadComponent,
    FwPopoverComponent,
  ],
})
export class TagSelectMenuComponent {
  private coreApi = inject(CoreApiService);

  value = input<TagFilter>({ contains: [], excludes: [] });
  resultType = input<SearchReturnType>();
  readonly valueChange = output<TagFilter>();

  form = new FormGroup({
    contains: new FormControl<string[]>([], { nonNullable: true }),
    excludes: new FormControl<string[]>([], { nonNullable: true }),
  });

  private openSnapshot: TagFilter | null = null;

  private containsSearch$ = new Subject<string>();
  private excludesSearch$ = new Subject<string>();

  private selectedContains = toSignal(this.form.controls.contains.valueChanges, { initialValue: [] as string[] });
  private selectedExcludes = toSignal(this.form.controls.excludes.valueChanges, { initialValue: [] as string[] });

  private rawContainsSuggestions = toSignal(
    this.containsSearch$.pipe(
      debounceTime(200),
      switchMap((search) => this.suggest(search)),
    ),
    { initialValue: [] as string[] },
  );
  private rawExcludesSuggestions = toSignal(
    this.excludesSearch$.pipe(
      debounceTime(200),
      switchMap((search) => this.suggest(search)),
    ),
    { initialValue: [] as string[] },
  );

  containsOptions = computed(() => {
    const used = new Set([...this.selectedContains(), ...this.selectedExcludes()]);
    return this.rawContainsSuggestions().filter((tag) => !used.has(tag));
  });

  excludesOptions = computed(() => {
    const used = new Set([...this.selectedContains(), ...this.selectedExcludes()]);
    return this.rawExcludesSuggestions().filter((tag) => !used.has(tag));
  });

  constructor() {
    effect(() => {
      const v = this.value();
      this.form.setValue({ contains: v.contains, excludes: v.excludes }, { emitEvent: false });
    });
  }

  onContainsSearchChange(search: string): void {
    this.containsSearch$.next(search);
  }

  onExcludesSearchChange(search: string): void {
    this.excludesSearch$.next(search);
  }

  reset(): void {
    this.form.setValue({ contains: [], excludes: [] });
    this.menuClosed();
  }

  isOpenChange(isOpenEvent: boolean): void {
    isOpenEvent ? this.menuOpened() : this.menuClosed();
  }

  private menuOpened(): void {
    const v = this.value();
    this.openSnapshot = {
      contains: [...v.contains],
      excludes: [...v.excludes],
    };
  }

  private menuClosed(): void {
    const current: TagFilter = {
      contains: this.form.controls.contains.value,
      excludes: this.form.controls.excludes.value,
    };
    if (!isEqual(this.openSnapshot, current)) {
      this.valueChange.emit(current);
    }
    this.openSnapshot = null;
  }

  private suggest(search: string): Observable<string[]> {
    const resultType = this.resultType();
    if (search.length < 3 || !resultType) {
      return of([]);
    }
    return this.coreApi.dataexplorer
      .suggest({
        structured_query: `${resultType}.tags = ${search}`,
      })
      .pipe(map((r) => r.suggestions.map((s) => s.value)));
  }
}
